import React from "react";
import { Lock, Activity, ShieldCheck, AlertTriangle } from "lucide-react";
import { fmtNum } from "@/lib/format";

const TONES = {
  good: { color: "#1EC62E", bg: "rgba(30,198,46,0.10)", border: "rgba(30,198,46,0.32)" },
  warn: { color: "#FFB800", bg: "rgba(255,184,0,0.10)", border: "rgba(255,184,0,0.32)" },
  info: { color: "#00E5FF", bg: "rgba(0,229,255,0.10)", border: "rgba(0,229,255,0.28)" },
};

// Compact on-chain trust signals. Everything here is derived from real curve/vesting data.
// `devLock` accepts either {tokens, days} (board map) or {dev_locked_tokens, dev_lock_days}.
export default function SafetyBadges({ coin, devLock = null, size = "sm", className = "" }) {
  const lockTokens = devLock?.tokens ?? devLock?.dev_locked_tokens ?? 0;
  const lockDays = devLock?.days ?? devLock?.dev_lock_days ?? 0;
  const supply = coin?.total_supply || 0;
  const badges = [];

  // 1) Liquidity status
  if (coin?.migrated) {
    badges.push({ icon: Lock, label: "LP Locked", tone: "good", title: "Liquidity migrated to Cetus and permanently locked" });
  } else {
    badges.push({ icon: Activity, label: "Bonding", tone: "info", title: "Trading on the fair bonding curve" });
  }

  // 2) Dev-locked supply
  if (lockTokens > 0) {
    const pct = supply > 0 ? (lockTokens / supply) * 100 : null;
    const pctTxt = pct == null ? fmtNum(lockTokens) : (pct > 0 && pct < 0.1 ? "<0.1%" : `${pct < 1 ? pct.toFixed(1) : Math.round(pct)}%`);
    badges.push({ icon: Lock, label: `Dev Locked ${pctTxt}`, sub: lockDays ? `${lockDays}d` : null, tone: "good", title: `Creator locked ${fmtNum(lockTokens)} tokens for ${lockDays} days` });
  }

  // 3) Dev first-buy (only meaningful once the first trade happened)
  if (coin?.first_trade_done) {
    badges.push(coin?.dev_bought_first
      ? { icon: AlertTriangle, label: "Dev Snipe", tone: "warn", title: "The creator made the first buy" }
      : { icon: ShieldCheck, label: "No Dev Snipe", tone: "good", title: "The creator did not snipe the first buy" });
  }

  if (badges.length === 0) return null;

  const sm = size === "sm";
  const pad = sm ? "px-1.5 py-0.5" : "px-2.5 py-1";
  const txt = sm ? "text-[9px]" : "text-[10px]";
  const iconSz = sm ? "w-2.5 h-2.5" : "w-3 h-3";

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="safety-badges">
      {badges.map((b, i) => {
        const t = TONES[b.tone];
        const Icon = b.icon;
        return (
          <span
            key={i}
            title={b.title}
            data-testid={`safety-badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/,"")}`}
            className={`inline-flex items-center gap-1 rounded-md border font-bold uppercase tracking-wide ${pad} ${txt}`}
            style={{ color: t.color, background: t.bg, borderColor: t.border }}
          >
            <Icon className={iconSz} />
            {b.label}
            {b.sub && <span className="opacity-70 font-mono normal-case">· {b.sub}</span>}
          </span>
        );
      })}
    </div>
  );
}
